import { useMemo } from 'react';
import { groupThreadsByCustomer } from '../../lib/clinic-logic';
import { cn } from '../../lib/utils';
import type { EmailThreadMetadata } from '../../types/email';

interface TriageCustomerSidebarProps {
  threads: EmailThreadMetadata[];
  selectedCustomerId: string | null;
  onSelect: (customerId: string | null) => void;
}

export function TriageCustomerSidebar({
  threads,
  selectedCustomerId,
  onSelect,
}: TriageCustomerSidebarProps) {
  const groups = useMemo(() => groupThreadsByCustomer(threads), [threads]);

  return (
    <div>
      <h3 className="text-sm font-semibold mb-4">Customers</h3>
      <div className="space-y-1.5 max-h-[calc(100vh-16rem)] overflow-y-auto pr-1">
        <button
          onClick={() => onSelect(null)}
          className={cn(
            'w-full flex items-center justify-between gap-2 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors',
            selectedCustomerId === null
              ? 'bg-primary text-primary-foreground '
              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
          )}
        >
          <span>All Customers</span>
          <span className="opacity-70">{threads.length}</span>
        </button>

        {groups.map((group) => {
          const showEmail = group.email && group.email !== group.label;
          return (
            <button
              key={group.customerId}
              onClick={() => onSelect(group.customerId)}
              className={cn(
                'w-full flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg text-xs font-medium transition-colors',
                selectedCustomerId === group.customerId
                  ? 'bg-primary text-primary-foreground '
                  : group.customerId === '__unknown__'
                    ? 'text-muted-foreground/60 hover:bg-muted/50 hover:text-muted-foreground'
                    : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
              )}
            >
              <div className="min-w-0 text-left">
                <div className="truncate">{group.label}</div>
                {showEmail && (
                  <div className="truncate text-[10px] font-normal opacity-70">{group.email}</div>
                )}
              </div>
              <span className="opacity-70 shrink-0">{group.threads.length}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
